import { GRID_CONFIG, sortByAuditUpdatedOn } from "../config";
import axios from "axios";
import React, { useEffect, useState } from "react";
import DataTable from "react-data-table-component";
import Swal from "sweetalert2";
import {
    FaSearch,
    FaAngleDoubleLeft,
    FaAngleLeft,
    FaAngleRight,
    FaAngleDoubleRight,
    FaSync,
    FaEdit,
    FaBan,
} from "react-icons/fa";

const Subscribers = () => {
    const [subscribers, setSubscribers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [perPage, setPerPage] = useState(GRID_CONFIG.defaultPageSize);
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedSubscriber, setSelectedSubscriber] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editEmail, setEditEmail] = useState("");

    useEffect(() => {
        fetchSubscribers();
    }, []);

    const fetchSubscribers = async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/alerts`);
            setSubscribers(sortByAuditUpdatedOn(res.data));
        } catch (err) {
            setSubscribers([]);
        }
        setLoading(false);
    };

    const formatDate = (dateString) => {
        if (!dateString) return "-";
        const date = new Date(dateString);
        const options = {
            year: "numeric",
            month: "2-digit",
            day: "2-digit",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
        };
        return date.toLocaleString("en-US", options);
    };

    const handleSearch = (e) => {
        setSearchTerm(e.target.value);
        setCurrentPage(1);
    };

    const filteredSubscribers = subscribers.filter(
        (sub) =>
            (sub.email && sub.email.toLowerCase().includes(searchTerm.toLowerCase())) ||
            (sub.id && String(sub.id).includes(searchTerm))
    );

    const totalPages = Math.ceil(filteredSubscribers.length / perPage);
    const paginatedData = filteredSubscribers.slice(
        (currentPage - 1) * perPage,
        currentPage * perPage
    );
    const fromRecord = (currentPage - 1) * perPage + 1;
    const toRecord = Math.min(currentPage * perPage, filteredSubscribers.length);
    const totalRecords = filteredSubscribers.length;

    const customStyles = {
        table: {
            style: {
                borderRadius: "8px",
                overflow: "hidden",
            },
        },
        headRow: {
            style: {
                backgroundColor: "var(--navy)",
                color: "white",
                fontWeight: "bold",
                fontSize: "16px",
                minHeight: "50px",
            },
        },
        headCells: {
            style: {
                paddingLeft: "25px",
                paddingRight: "25px",
            },
        },
        cells: {
            style: {
                paddingLeft: "25px",
                paddingRight: "25px",
                fontSize: "14px",
            },
        },
        rows: {
            style: {
                cursor: "default",
                minHeight: "45px",
                "&:hover": {
                    backgroundColor: "#f1f8ff",
                },
            },
            stripedStyle: {
                backgroundColor: "#F5F7FA",
            },
        },
        pagination: {
            style: {
                borderTop: "none",
            },
        },
    };

    const columns = [
        { name: "ID", selector: (row) => row.id, sortable: true, width: "240px" },
        { name: "Email", selector: (row) => row.email, sortable: true, minWidth: "480px" },
        {
            name: "Subscribed At",
            selector: (row) => formatDate(row.subscribed_at),
            sortable: true,
        },
        {
            name: "Edit",
            cell: (row) => (
                <div className="flex items-center justify-center">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleEdit(row);
                        }}
                        title="Edit"
                        aria-label="Edit"
                        className="text-navy hover:text-navy px-2 py-1"
                    >
                        <FaEdit />
                    </button>
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: "120px",
        },
        {
            name: "Unsubscribe",
            cell: (row) => (
                <div className="flex items-center justify-center">
                    <button
                        onClick={(e) => {
                            e.stopPropagation();
                            handleUnsubscribe(row);
                        }}
                        title="Unsubscribe"
                        aria-label="Unsubscribe"
                        className="text-red-600 hover:text-red-800 px-2 py-1"
                    >
                        <FaBan />
                    </button>
                </div>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: "120px",
        },
    ];

    const handleEdit = (row) => {
        const sub = row || selectedSubscriber;
        if (!sub) {
            Swal.fire({ icon: "info", title: "No subscriber selected" });
            return;
        }
        setSelectedSubscriber(sub);
        setEditEmail(sub.email || "");
        setIsEditModalOpen(true);
    };

    const handleUpdate = async () => {
        if (!selectedSubscriber) return;
        if (!editEmail || !/^\S+@\S+\.\S+$/.test(editEmail)) {
            Swal.fire({ icon: "error", title: "Invalid email" });
            return;
        }
        try {
            await axios.put(
                `${process.env.REACT_APP_BACKEND_URL}/api/alerts/${selectedSubscriber.id}`,
                { email: editEmail }
            );
            fetchSubscribers();
            setIsEditModalOpen(false);
            setSelectedSubscriber(null);
            Swal.fire({ icon: "success", title: "Updated", text: "Subscriber updated." });
        } catch (err) {
            console.error(err);
            Swal.fire({ icon: "error", title: "Failed", text: "Failed to update subscriber." });
        }
    };

    const handleUnsubscribe = async (row) => {
        const sub = row || selectedSubscriber;
        if (!sub) {
            Swal.fire({ icon: "info", title: "No subscriber selected" });
            return;
        }

        const result = await Swal.fire({
            title: `Unsubscribe ${sub.email}?`,
            text: "Do you want to unsubscribe this user?",
            icon: "warning",
            showCancelButton: true,
            confirmButtonText: "Yes, unsubscribe",
            confirmButtonColor: "var(--navy)",
            cancelButtonText: "Cancel",
        });

        if (!result.isConfirmed) return;

        try {
            await axios.delete(`${process.env.REACT_APP_BACKEND_URL}/api/alerts/${sub.id}`);
            fetchSubscribers();
            setSelectedSubscriber(null);
            Swal.fire({ icon: "success", title: "Unsubscribed", text: "Subscriber removed." });
        } catch (err) {
            console.error(err);
            Swal.fire({ icon: "error", title: "Failed", text: "Failed to unsubscribe." });
        }
    };

    const handleExportCSV = () => {
        const rows = filteredSubscribers.map((s) => ({
            ID: s.id ?? "",
            Email: s.email ?? "",
            SubscribedAt: s.subscribed_at ? formatDate(s.subscribed_at) : "",
        }));

        if (!rows.length) {
            Swal.fire({ icon: "info", title: "No data to export" });
            return;
        }

        const header = Object.keys(rows[0]);
        const csv = [
            header.join(","),
            ...rows.map((r) => header.map((h) => `"${String(r[h]).replace(/"/g, '""')}"`).join(",")),
        ].join("\n");

        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", "subscribers_export.csv");
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const CustomPagination = () => (
        <div className="flex justify-between items-center px-2 py-3 text-sm text-gray-700 mt-4">
            <span>
                Showing {filteredSubscribers.length === 0 ? 0 : fromRecord} to {toRecord} of {totalRecords} records
            </span>
            <div className="flex gap-2 text-lg items-center">
                <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage(1)}
                    className="disabled:opacity-40"
                >
                    <FaAngleDoubleLeft />
                </button>
                <button
                    disabled={currentPage === 1}
                    onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                    className="disabled:opacity-40"
                >
                    <FaAngleLeft />
                </button>
                <span className="text-sm px-2">
                    Page {currentPage} of {totalPages}
                </span>
                <button
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                    className="disabled:opacity-40"
                >
                    <FaAngleRight />
                </button>
                <button
                    disabled={currentPage === totalPages}
                    onClick={() => setCurrentPage(totalPages)}
                    className="disabled:opacity-40"
                >
                    <FaAngleDoubleRight />
                </button>
            </div>
        </div>
    );

    return (
        <div
            className="aqi-grid-page"
            style={{
                padding: "30px",
                width: "90%",
                minHeight: "80vh",
                height: "auto",
                margin: "30px auto",
                boxShadow: "0 0 20px rgba(0,0,0,0.1)",
                borderRadius: "8px",
                backgroundColor: "white",
                position: "relative",
                overflowX: "hidden",
            }}
        >
            <div className="container mx-auto p-5">
                <div className="flex flex-col gap-2 mb-4">
                    <h2
                        style={{
                            color: "var(--navy)",
                            fontSize: "large",
                            fontFamily: "large",
                            margin: 0,
                        }}
                        className="mb-2"
                    >
                        SUBSCRIBERS
                    </h2>
                    <div className="aqi-grid-toolbar">
                        <div className="mr-2">
                            <select
                                style={{
                                    padding: "0.375rem 2rem 0.375rem 0.75rem",
                                    color: "var(--navy)",
                                    backgroundColor: "#F5F7FA",
                                    border: "2px solid #C2D7E2",
                                    borderRadius: "0.375rem",
                                    fontWeight: "500",
                                    appearance: "none",
                                    backgroundImage:
                                        "var(--navy-select-chevron)",
                                    backgroundRepeat: "no-repeat",
                                    backgroundPosition: "right 0.5rem center",
                                    backgroundSize: "16px 12px",
                                    transition: "all 0.2s ease",
                                    cursor: "pointer",
                                    outline: "none",
                                }}
                                className="aqi-grid-control hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                                value={perPage}
                                onChange={(e) => {
                                    setPerPage(Number(e.target.value));
                                    setCurrentPage(1);
                                }}
                            >
                                {GRID_CONFIG.pageSizeOptions.map((option) => (
                                    <option key={option} value={option}>{option}</option>
                                ))}
                            </select>

                        </div>
                        <div className="aqi-grid-search">
                            <FaSearch
                                className="absolute left-3 top-2.5"
                                style={{ color: "var(--navy)" }}
                            />
                            <input
                                type="text"
                                placeholder="Search subscribers..."
                                value={searchTerm}
                                onChange={handleSearch}
                                style={{
                                    paddingLeft: "2.5rem",
                                    paddingRight: "0.75rem",
                                    paddingTop: "0.5rem",
                                    paddingBottom: "0.5rem",
                                    borderRadius: "0.375rem",
                                    border: "2px solid #C2D7E2",
                                    backgroundColor: "#F5F7FA",
                                    transition: "all 0.2s ease",
                                    outline: "none",
                                    color: "var(--navy)",
                                }}
                                className="aqi-grid-search-input hover:border-navy focus:border-navy focus:ring-1 focus:ring-navy focus:shadow-sm"
                            />
                        </div>
                        <div className="aqi-grid-toolbar-actions">
                            <button
                                onClick={handleExportCSV}
                                title="Export CSV"
                                className="aqi-grid-button aqi-grid-button--primary"
                            >
                                Export
                            </button>
                        </div>
                    </div>
                </div>
                <div style={{ overflowX: "auto", width: "100%" }}>
                    <DataTable
                        columns={columns}
                        data={paginatedData}
                        conditionalRowStyles={[
                            {
                                when: (row) => selectedSubscriber && row.id === selectedSubscriber.id,
                                style: {
                                    backgroundColor: "#e6f2fb",
                                },
                            },
                        ]}
                        highlightOnHover
                        pointerOnHover
                        pagination={false}
                        responsive
                        striped
                        fixedHeader
                        fixedHeaderScrollHeight="500px"
                        paginationContainerStyles={{ display: "none" }}
                        subHeaderWrap={false}
                        customStyles={customStyles}
                    />
                </div>
                {/* Edit Subscriber Modal */}
                {isEditModalOpen && selectedSubscriber && (
                    <div className="fixed inset-0 flex justify-center items-center bg-black bg-opacity-50 z-50">
                        <div className="bg-white p-6 rounded-lg shadow-lg w-full max-w-4xl max-h-[80vh] overflow-y-auto">
                            <h2 className="text-xl font-semibold mb-4">Edit Subscriber</h2>
                            <div>
                                <label className="block mb-2">Email</label>
                                <input
                                    type="email"
                                    name="email"
                                    value={editEmail}
                                    onChange={(e) => setEditEmail(e.target.value)}
                                    className="w-full p-2 border border-gray-300 rounded"
                                />
                            </div>
                            <div className="mt-4 flex justify-end gap-2">
                                <button
                                    onClick={() => setIsEditModalOpen(false)}
                                    className="bg-gray-300 text-white px-4 py-2 rounded"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handleUpdate}
                                    className="aqi-primary-action rounded bg-navy px-4 py-2 text-white"
                                >
                                    Save
                                </button>
                            </div>
                        </div>
                    </div>
                )}
                <CustomPagination />
            </div>
        </div>
    );
};

export default Subscribers;
